import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Plus, ChevronRight, Truck, FileDown, Database, Fuel, Archive } from "lucide-react";
import { getVehicles, conformityReportUrl, costsCsvUrl, vehicleThumbSrc } from "@/lib/api";
import { fmtKm } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { useVehicleDrawer } from "@/context/VehicleDrawerContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/rbac";
import NewVehicleDialog from "@/components/NewVehicleDialog";
import SyncButton from "@/components/SyncButton";
import ConfigBanner from "@/components/ConfigBanner";
import AstraStatusDialog from "@/components/AstraStatusDialog";
import FleetEnrichDialog from "@/components/FleetEnrichDialog";
import FleetConsumptionDialog from "@/components/FleetConsumptionDialog";
import QueryErrorState from "@/components/QueryErrorState";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { lvl } from "@/lib/status";
import { cn } from "@/lib/utils";

export default function Vehicles() {
  const { openVehicle } = useVehicleDrawer();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = can(user, "vehicles.create");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [astraOpen, setAstraOpen] = useState(false);
  const [fleetOpen, setFleetOpen] = useState(false);
  const [consoOpen, setConsoOpen] = useState(false);
  const { data: vehicles = [], isLoading, isError, error } = useQuery({ queryKey: ["vehicles"], queryFn: getVehicles });

  const filtered = vehicles.filter((v) => {
    const hay = `${v.plaque} ${v.marque} ${v.modele} ${v.vin || ""} ${v.base || ""} ${v.groupe || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Véhicules
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            {vehicles.length} véhicule{vehicles.length > 1 ? "s" : ""} · cliquez sur une plaque pour ouvrir la fiche administrative.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-0 flex-1 sm:flex-none">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="vehicle-search"
              placeholder="Rechercher un véhicule, une plaque..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 sm:w-72"
            />
          </div>
          {canCreate && (
            <Button data-testid="add-vehicle-btn" onClick={() => setCreateOpen(true)} className="gap-2 bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4" /> Véhicule
            </Button>
          )}
          <Button
            data-testid="conso-open-btn"
            variant="outline"
            className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-50"
            onClick={() => setConsoOpen(true)}
          >
            <Fuel className="h-4 w-4" /> Consommation
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="astra-menu-btn" variant="outline" className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-50">
                <Database className="h-4 w-4" /> Base technique
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="fleet-enrich-menu-item" onClick={() => setFleetOpen(true)}>
                Enrichir la flotte (ASTRA)
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="astra-status-menu-item" onClick={() => setAstraOpen(true)}>
                État des données officielles
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="export-menu-btn" variant="outline" className="gap-2 border-slate-300 text-slate-700 hover:bg-slate-50">
                <FileDown className="h-4 w-4" /> Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="report-pdf-btn" onClick={() => window.open(conformityReportUrl(), "_blank", "noopener")}>
                Rapport de conformité (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="report-csv-btn" onClick={() => window.open(costsCsvUrl(), "_blank", "noopener")}>
                Coûts de la flotte (CSV)
              </DropdownMenuItem>
              {can(user, "pages.archives") && (
                <DropdownMenuItem data-testid="archives-menu-item" onClick={() => navigate("/archives")}>
                  <Archive className="mr-2 h-4 w-4 text-slate-400" /> Véhicules archivés
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <SyncButton />
        </div>
      </div>

      <ConfigBanner />

      {isError && <QueryErrorState error={error} testId="vehicles-error" />}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Véhicule", "Affectation / km", "Leasing", "Assurance", "Contrôle", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">Chargement…</td></tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-400">Aucun véhicule trouvé.</td></tr>
              )}
              {filtered.map((v) => {
                const m = v.metrics || {};
                const thumb = vehicleThumbSrc(v);
                return (
                  <tr key={v.id} className="group cursor-pointer transition-colors hover:bg-slate-50" data-testid={`vehicle-row-${v.id}`} onClick={() => openVehicle(v.id)}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-[76px] shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 shadow-sm">
                          {thumb ? (
                            <img src={thumb} alt={v.plaque} loading="lazy"
                                 onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "flex"; }}
                                 className="h-full w-full object-cover" />
                          ) : null}
                          <div className="h-full w-full items-center justify-center text-slate-300" style={{ display: thumb ? "none" : "flex" }}>
                            <Truck className="h-5 w-5" />
                          </div>
                        </div>
                        <div>
                          <button
                            onClick={(e) => { e.stopPropagation(); openVehicle(v.id); }}
                            data-testid={`vehicle-plate-link-${v.id}`}
                            className="font-display text-[15px] font-bold tracking-tight text-slate-900 transition-colors group-hover:text-slate-700"
                          >
                            {v.plaque}
                          </button>
                          <p className="text-xs text-slate-500">{[v.marque, v.modele].filter(Boolean).join(" ")}{v.annee ? ` · ${v.annee}` : ""}</p>
                          {v.navixy_absent && (
                            <span data-testid={`vehicle-navixy-absent-${v.id}`}
                                  className="mt-0.5 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                              Retiré de Navixy
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <p className="text-sm font-semibold text-slate-700">{fmtKm(v.kilometrage)}</p>
                      <p className="text-xs text-slate-400">{[v.base, v.groupe].filter(Boolean).join(" · ") || "—"}</p>
                    </td>
                    <td className="px-4 py-2.5"><StatusBadge level={m.leasing?.level} days={m.leasing?.days_remaining} showDays /></td>
                    <td className="px-4 py-2.5"><StatusBadge level={m.assurance?.level} days={m.assurance?.days_remaining} showDays /></td>
                    <td className="px-4 py-2.5"><StatusBadge level={m.controle?.level} days={m.controle?.days_remaining} showDays /></td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); openVehicle(v.id); }}
                        data-testid={`open-vehicle-${v.id}`}
                        className={cn("inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-900 hover:text-white", m.compliant ? "" : `ring-1 ${lvl(m.overall).ring}`)}
                        aria-label="Ouvrir la fiche"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <NewVehicleDialog open={createOpen} onOpenChange={setCreateOpen} />
      <AstraStatusDialog open={astraOpen} onOpenChange={setAstraOpen} />
      <FleetEnrichDialog open={fleetOpen} onOpenChange={setFleetOpen} />
      <FleetConsumptionDialog open={consoOpen} onOpenChange={setConsoOpen} />
    </div>
  );
}
